import styled, { css } from 'styled-components';

import { IconCross, IconTick } from '@xing-com/icons';
import { zIndexContentLayer2 } from '@xing-com/layout-tokens';
import {
  motionEasingStandard,
  motionTimeM,
  motionTimeS,
  motionTimeXXS,
  scale010,
  scale080,
  scale110,
  scale150,
  spaceXS,
  spaceXXS,
  xdlColorControlBorder,
  xdlColorControlFill,
  xdlColorDisabledSoft,
  xdlColorError,
  xdlColorFocusRing,
  xdlColorSelected,
} from '@xing-com/tokens';

type ToggleTProps = {
  $checked?: boolean;
  $disabled?: boolean;
  $error?: boolean;
};

type HandleTProps = {
  $checked?: boolean;
  $disabled?: boolean;
  $error?: boolean;
};

type IconTProps = {
  $checked?: boolean;
  $disabled?: boolean;
  $error?: boolean;
};

// *** Components ***
export const ToggleContainer = styled.div`
  display: inline-flex;
  height: ${scale110};
  position: relative;
  width: ${scale150};
`;

export const Toggle = styled.input<ToggleTProps>`
  appearance: none;
  border-radius: calc(${scale110} / 2); /* toggle height / 2  */
  border-style: solid;
  border-width: ${scale010};
  cursor: pointer;
  display: inline-flex;
  height: ${scale110};
  margin: 0;
  position: relative;
  transition-duration: ${motionTimeM};
  transition-property: all;
  transition-timing-function: ${motionEasingStandard};
  width: ${scale150};

  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  ${(props) => css`
    ${props.$checked
      ? css`
          background-color: ${xdlColorSelected};
          border-color: ${xdlColorSelected};

          ${props.$error &&
          css`
            background-color: ${xdlColorError};
            border-color: ${xdlColorError};
          `}

          ${props.$disabled &&
          css`
            background-color: ${xdlColorDisabledSoft};
            border-color: ${xdlColorDisabledSoft};
            cursor: default;
          `}
        `
      : css`
          background-color: ${xdlColorControlFill};
          border-color: ${xdlColorControlBorder};

          ${props.$error &&
          css`
            border-color: ${xdlColorError};
          `}

          ${props.$disabled &&
          css`
            background-color: ${xdlColorControlFill};
            border-color: ${xdlColorDisabledSoft};
            cursor: default;
          `}
        `};
  `};
`;

export const Handle = styled.div<HandleTProps>`
  align-items: center;
  border-radius: 50%;
  box-sizing: border-box;
  color: ${xdlColorSelected};
  display: flex;
  height: ${scale080};
  inset-block-start: 50%;
  inset-inline-start: ${spaceXS};
  justify-content: center;
  pointer-events: none;
  position: absolute;
  transition-delay: ${motionTimeXXS};
  transition-duration: ${motionTimeS};
  transition-property: all;
  transition-timing-function: ${motionEasingStandard};
  width: ${scale080};
  z-index: ${zIndexContentLayer2};

  ${(props) => css`
    ${props.$checked
      ? css`
          transform: translate3d(calc(${scale110} * 0.75), -50%, 0);
          background-color: ${xdlColorControlFill};

          ${props.$error &&
          css`
            background-color: ${xdlColorControlFill};
          `};

          ${props.$disabled &&
          css`
            background-color: ${xdlColorControlFill};
          `};
        `
      : css`
          transform: translate3d(0, -50%, 0);
          background-color: ${xdlColorControlBorder};

          ${props.$error &&
          css`
            background-color: ${xdlColorError};
          `};

          ${props.$disabled &&
          css`
            background-color: ${xdlColorDisabledSoft};
          `};
        `}
  `};
`;

export const CheckIcon = styled(IconTick)<IconTProps>`
  color: ${xdlColorSelected};
  position: absolute;
  transition-delay: ${motionTimeXXS};
  transition-duration: ${motionTimeS};
  transition-property: all;
  transition-timing-function: ${motionEasingStandard};

  ${(props) => css`
    ${props.$checked
      ? css`
          opacity: 1;

          ${props.$error &&
          css`
            color: ${xdlColorError};
          `};

          ${props.$disabled &&
          css`
            color: ${xdlColorDisabledSoft};
          `};
        `
      : css`
          opacity: 0;
        `}
  `};
`;

export const CrossIcon = styled(IconCross)<IconTProps>`
  color: ${xdlColorControlFill};
  position: absolute;
  transition-delay: ${motionTimeXXS};
  transition-duration: ${motionTimeS};
  transition-property: all;
  transition-timing-function: ${motionEasingStandard};

  ${(props) => css`
    ${props.$checked
      ? css`
          opacity: 0;
        `
      : css`
          opacity: 1;

          ${props.$error &&
          css`
            color: ${xdlColorControlFill};
          `};

          ${props.$disabled &&
          css`
            color: ${xdlColorControlFill};
          `};
        `}
  `};
`;
